import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

import "swiper/css";
import "swiper/css/pagination";

const ReviewCarousel = () => {
  const { t } = useTranslation();

  const reviews = [
    { id: "nadia", stars: 5 },
    { id: "virginie", stars: 5 },
    { id: "catherine", stars: 5 },
    { id: "manjit", stars: 5 },
    { id: "diego", stars: 5 },
  ];

  return (
    <section className="relative py-40 md:py-52 px-6 overflow-hidden bg-cream">
      {/* BACKGROUND TEXTURE */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-soft-light bg-[url('/assets/textures/grain.png')]"
        aria-hidden="true"
      />

      {/* SUBTLE GOLD SHAPES */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        className="absolute -left-10 top-12 w-[340px] h-[340px] rounded-full border border-[#a5694f]/30"
        aria-hidden="true"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
        className="absolute -right-10 bottom-12 w-[420px] h-[420px] rounded-full border border-[#a5694f]/20"
        aria-hidden="true"
      />

      {/* ACCENT DOT */}
      <motion.div
        animate={{ opacity: [0.35, 0.85, 0.35] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute left-24 top-28 w-2 h-2 bg-[#a5694f] rounded-full shadow-[0_0_14px_#a5694f]"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto">
        {/* ================= HEADER (Featured Style) ================= */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-24"
        >
          <span className="block font-oswald font-light uppercase tracking-[0.45em] text-stone-400 text-[10px] mb-6">
            {t("home.reviews.eyebrow", "Trusted by clients")}
          </span>

          <h2 className="font-oswald font-light uppercase tracking-wide text-4xl sm:text-5xl md:text-6xl text-stone-800 leading-[1.1]">
            {t("home.reviews.title", "What our clients say")}
          </h2>

          <p className="mt-10 font-playfair italic text-base sm:text-lg text-stone-500/70">
            {t(
              "home.reviews.subtitle",
              "Every story reflects our commitment to excellence, discretion, and results."
            )}
          </p>
        </motion.div>

        {/* ================= SWIPER ================= */}
        <Swiper
          aria-label="Client reviews carousel"
          modules={[Autoplay, Pagination]}
          spaceBetween={28}
          slidesPerView={1}
          autoplay={{ delay: 6500, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="pb-20 !overflow-visible !flex !items-stretch"
        >
          {reviews.map((review, idx) => (
            <SwiperSlide key={review.id} className="h-full">
              <motion.div
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ once: true, margin: "-50px" }}
                style={{ willChange: "transform, opacity" }}
                className="
                  relative h-full flex flex-col rounded-[30px]
                  bg-[#5D504D]
                  p-10 md:p-12
                  text-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)]
                  border border-white/5 hover:border-white/15 hover:shadow-[0_20px_50px_-10px_rgba(0,0,0,0.4)]
                  transition-all duration-500
                  overflow-hidden group
                "
              >
                {/* TOP GLOW */}
                <div
                  className="
                    absolute top-0 right-0 w-52 h-52
                    bg-gradient-to-br from-white/10 to-transparent
                    rounded-full -mr-16 -mt-16 blur-2xl
                    group-hover:scale-110 transition-transform duration-700
                  "
                  aria-hidden="true"
                />

                {/* GOLD BORDER */}
                <div
                  className="
                    absolute inset-0 rounded-[30px]
                    ring-1 ring-[#a5694f]/20
                    group-hover:ring-[#a5694f]/40 transition-all duration-500
                  "
                  aria-hidden="true"
                />

                {/* QUOTE MARK */}
                <div className="absolute left-10 top-10 text-[#a5694f] text-6xl font-oswald leading-none opacity-80" aria-hidden="true">
                  “
                </div>

                {/* HEADER */}
                <div className="relative z-10 flex justify-between items-start mb-10">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-xl md:text-2xl font-oswald font-light tracking-widest uppercase text-white/90">
                      {t(`home.reviews.items.${review.id}.name`)}
                    </h3>
                    <span className="text-xs tracking-[0.22em] uppercase text-white/40">
                      {t("home.reviews.verified", "Verified client")}
                    </span>
                  </div>

                  <div className="flex gap-1.5 pt-1" aria-label={`${review.stars} stars`}>
                    {[...Array(review.stars)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        fill="#fff"
                        color="#fff"
                        className="opacity-90"
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                </div>

                {/* COMMENT */}
                <p className="relative z-10 font-playfair italic text-lg md:text-xl leading-relaxed text-white/80 flex-grow">
                  {t(`home.reviews.items.${review.id}.comment`)}
                </p>

                {/* FOOTER */}
                <div className="relative z-10 mt-10 pt-8 border-t border-white/10 flex items-center gap-4 mt-auto">
                  <div className="w-11 h-11 bg-white border border-white/10 rounded-full flex items-center justify-center shadow-sm">
                    <img
                      src="https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/google-color-icon.png"
                      alt="Google"
                      className="w-5 h-5"
                      width={20}
                      height={20}
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                  </div>

                  <div className="flex flex-col">
                    <span className="text-[10px] font-oswald tracking-[0.3em] uppercase text-white/40 leading-none mb-1">
                      {t("home.reviews.sourceLabel", "Reviewed on")}
                    </span>
                    <span className="text-xs font-bold text-white/80">Google Maps</span>
                  </div>

                  <div className="ml-auto text-xs font-oswald tracking-[0.25em] uppercase text-white/40">
                    {t("home.reviews.ratingLabel", "5.0 Rating")}
                  </div>
                </div>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* ================= CTA ================= */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mt-10 flex justify-center px-4"
        >
          <a
            href="https://share.google/8sgaQ4Pmj4ThZrxvx"
            target="_blank"
            rel="noopener noreferrer"
            className="
              group relative flex items-center gap-4
              px-5 py-4 sm:px-6 sm:py-4
              rounded-2xl
              bg-white/60 backdrop-blur-md
              shadow-md hover:shadow-xl
              transition-all duration-500
              ring-1 ring-stone-200 hover:ring-[#a5694f]/40
              max-w-full
            "
          >
            {/* Hover glow */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#a5694f]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Google icon */}
            <img
              src="https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/google-color-icon.png"
              alt="Google"
              className="w-9 h-9 sm:w-10 sm:h-10 drop-shadow-sm relative z-10 shrink-0"
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
            />

            {/* Text */}
            <div className="relative z-10 flex flex-col border-l border-stone-200 pl-4 sm:pl-5 min-w-0">
              <span className="text-stone-500 text-[10px] sm:text-xs font-oswald tracking-[0.25em] uppercase leading-tight">
                {t("home.reviews.google_link_label", "Read all reviews on")}
              </span>

              <span className="text-stone-800 text-xl sm:text-2xl font-bold font-playfair group-hover:text-[#a5694f] transition-colors leading-tight truncate">
                Google
              </span>
            </div>

            {/* Arrow */}
            <span className="relative z-10 ml-2 sm:ml-4 text-xs sm:text-sm font-oswald tracking-[0.22em] uppercase text-stone-400 group-hover:text-[#a5694f] transition-colors shrink-0">
              →
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewCarousel;
